import React, { useEffect, useState } from 'react';
import MaterialTable, {
    MTableBody,
    MTableFilterRow,
    MTableToolbar,
} from 'material-table';
import LocationDataService from '../../../services/LocationService';
import axios from 'axios';
import { API_URL } from '../../../utils/setApiUrl.js';
import 'date-fns';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Paper } from '@material-ui/core';
import { Settings } from 'luxon';
import DescriptionIcon from '@material-ui/icons/Description';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx/dist/xlsx.full.min.js';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/bg';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getAppTimezone, toTzDate } from '../../../utils/timezone';
import { getMaterialTableExportData } from '../../../utils/getMaterialTableExportData';
import {
    formatToolbarDateForApi,
    getDefaultToolbarEndDate,
    normalizeToolbarEndDate,
    normalizeToolbarStartDate,
} from '../../../utils/reportDateDefaults';
import { useDepartmentsOptions } from '../../../utils/useDepartmentsOptions';
import { reportDatePickerSlotProps } from '../../../utils/reportDatePickerSlotProps';
Settings.defaultZone = 'Asia/Istanbul';

const SPACED_DATE_FORMAT = 'DD/MMM/YYYY H:MM:ss';

const useStyles = makeStyles(theme => ({
    button: {
        marginLeft: theme.spacing(1),
        marginTop: theme.spacing(2.5),
    },
    grid: {
        marginLeft: theme.spacing(2),
    },
    toolbar: { marginLeft: theme.spacing(10) },
    absoluteContainer: {
        position: 'absolute',
        zIndex: 9,
        width: '45vw',
        [theme.breakpoints.down('md')]: {
            position: 'initial',
        },
    },
}));

const DiscrepanciesTable = () => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const departmentsOptions = useDepartmentsOptions();

    // Get user role from localStorage as a simple string
    const [isAnalyzer, setIsAnalyzer] = useState(false);

    // Initialize user role from localStorage when component mounts
    useEffect(() => {
        const userRole = localStorage.getItem('level');
        // Check if the role is 'analyzer'
        if (userRole) {
            setIsAnalyzer(userRole === 'analyzer');
        }
    }, []);

    const locData = JSON.parse(localStorage.getItem('loc'));

    const client = axios.create({
        baseURL: API_URL,
    });

    // ! Gap Filter Start
    const [locations, setLocations] = useState([]);

    useEffect(() => {
        retrieveLocations();
    }, []);

    const retrieveLocations = () => {
        LocationDataService.getAll()
            .then(response => {
                setLocations(response.data);
                console.log(response.data);
            })
            .catch(e => {
                console.log(e);
            });
    };

    const [locationValue, setLocationValue] = React.useState('');
    const [locationInputValue, setLocationInputValue] = React.useState('');

    var x = new Date();
    const [selectedStartDate, handleStartDateChange] = useState(null);

    const [selectedEndDate, handleEndDateChange] = useState(
        getDefaultToolbarEndDate
    );

    const [filteredDay, setFilteredDay] = useState([]);
    const [dataFetched, setDataFetched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Extend dayjs with plugins
    dayjs.extend(utc);
    dayjs.extend(timezone);

    // Function to get date format and locale based on current language
    const getDateFormatAndLocale = () => {
        const language = i18n.language;
        switch (language) {
            case 'en':
                return {
                    format: 'MM/DD/YYYY HH:mm:ss',
                    locale: 'en',
                    dateFormatExport: date => {
                        return `${date.getMonth() +
                            1}/${date.getDate()}/${date.getFullYear()}, ${date
                            .getHours()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')}`;
                    },
                };
            case 'es':
                return {
                    format: 'DD/MM/YYYY HH:mm:ss',
                    locale: 'es',
                    dateFormatExport: date => {
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}/${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0')}/${date.getFullYear()}, ${date
                            .getHours()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')}`;
                    },
                };
            case 'bg':
            default:
                return {
                    format: 'DD.MM.YYYY г., HH:mm:ss ч.',
                    locale: 'bg',
                    dateFormatExport: date => {
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}.${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0')}.${date.getFullYear()} г., ${date
                            .getHours()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')} ч.`;
                    },
                };
        }
    };

    // Get the current date format configuration
    const [dateConfig, setDateConfig] = useState(getDateFormatAndLocale());

    // Update date config when language changes
    useEffect(() => {
        setDateConfig(getDateFormatAndLocale());
    }, [i18n.language]);

    const renderProblemCell = rowData => {
        if (rowData.problem === 'Липсва вход') {
            return t('reports.discrepanciesReport.missingEntry');
        }
        if (rowData.problem === 'Липсва изход') {
            return t('reports.discrepanciesReport.missingExit');
        }
        return rowData.problem;
    };

    const renderFirstEntryTimeCell = rowData => {
        if (rowData.first_entry_time === '<<') {
            return t('reports.discrepanciesReport.missingEntry');
        }
        if (
            rowData.first_entry_time &&
            rowData.first_entry_time !== 'null' &&
            rowData.first_entry_time !== 'empty'
        ) {
            if (i18n.language === 'bg') {
                const date = toTzDate(rowData.first_entry_time);
                return `${date
                    .getDate()
                    .toString()
                    .padStart(2, '0')}.${(date.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}.${date.getFullYear()} г., ${date
                    .getHours()
                    .toString()
                    .padStart(2, '0')}:${date
                    .getMinutes()
                    .toString()
                    .padStart(2, '0')}:${date
                    .getSeconds()
                    .toString()
                    .padStart(2, '0')} ч.`;
            }
            return dayjs(rowData.first_entry_time)
                .tz(getAppTimezone())
                .locale(dateConfig.locale)
                .format(dateConfig.format);
        }
        return '';
    };

    const renderLastExitTimeCell = rowData => {
        if (rowData.last_exit_time === '>>') {
            return t('reports.discrepanciesReport.missingExit');
        }
        if (
            rowData.last_exit_time &&
            rowData.last_exit_time !== 'null' &&
            rowData.last_exit_time !== 'empty'
        ) {
            if (i18n.language === 'bg') {
                const date = toTzDate(rowData.last_exit_time);
                return `${date
                    .getDate()
                    .toString()
                    .padStart(2, '0')}.${(date.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}.${date.getFullYear()} г., ${date
                    .getHours()
                    .toString()
                    .padStart(2, '0')}:${date
                    .getMinutes()
                    .toString()
                    .padStart(2, '0')}:${date
                    .getSeconds()
                    .toString()
                    .padStart(2, '0')} ч.`;
            }
            return dayjs(rowData.last_exit_time)
                .tz(getAppTimezone())
                .locale(dateConfig.locale)
                .format(dateConfig.format);
        }
        return '';
    };

    const retrieveFilteredDay = async () => {
        setIsLoading(true); // Start loading

        setFilteredDay([]);

        const formattedStartDate = formatToolbarDateForApi(selectedStartDate);
        const newEndDate = formatToolbarDateForApi(selectedEndDate);

        if (Array.isArray(locData)) {
            const joinedValues = locData.join(',');
            const request = client.get(
                `/discrepancies/date/${formattedStartDate}/${newEndDate}/loc/${joinedValues}`
            );
            try {
                const response = await request;
                if (response && response.data) {
                    // Ensure response has the data you need
                    setFilteredDay(response.data); // Assuming the relevant data is directly in response.data
                    setDataFetched(true);
                } else {
                    throw new Error('No data found in response');
                }
                setOpen(false);
            } catch (error) {
                setIserror(error.response);
                setErrorMessages('Не бяха намарени отклонения!'); // You can handle the JSX part where you render this message
                console.error(error.response);
            } finally {
                setIsLoading(false); // End loading regardless of success or failure
            }
        } else if (locData.id === 'all') {
            const request = client.get(
                `/discrepancies/date/${formattedStartDate}/${newEndDate}/all`
            );
            try {
                const response = await request;
                if (response && response.data) {
                    // Ensure response has the data you need
                    setFilteredDay(response.data); // Assuming the relevant data is directly in response.data
                    setDataFetched(true);
                } else {
                    throw new Error('No data found in response');
                }
                setOpen(false);
            } catch (error) {
                setIserror(error.response);
                setErrorMessages('Не бяха намарени отклонения!'); // You can handle the JSX part where you render this message
                console.error(error.response);
            } finally {
                setIsLoading(false); // End loading regardless of success or failure
            }
        }
    };

    // const retrieveFilteredDay = async () => {
    //     setFilteredDay([]);
    //
    //     setLoading(true);
    //
    //     const getDaysArray = (start, end) => {
    //         const dates = [];
    //         let currentDate = new Date(start);
    //         while (currentDate <= new Date(end)) {
    //             dates.push(new Date(currentDate));
    //             currentDate.setDate(currentDate.getDate() + 1);
    //         }
    //         return dates;
    //     };
    //
    //     const daylist = getDaysArray(selectedStartDate, selectedEndDate);
    //     setDates(daylist);
    //
    //     const requests = daylist.map((d) => {
    //         return client.get(
    //             `/discrepancies/date/${JSON.stringify(d)
    //                 .split('T')[0]
    //                 .replace(/\"/g, '')}`
    //         );
    //     });
    //
    //     try {
    //         const responses = await Promise.all(requests);
    //         const allData = responses.flatMap((response) => response.data);
    //         setFilteredDay(allData);
    //         setDataFetched(true);
    //         setOpen(false);
    //     } catch (error) {
    //         setIserror(error.response);
    //         setErrorMessages(
    //             <Alert severity="error">Не бяха намарени отклонения!</Alert>
    //         );
    //         console.log(error.response);
    //     } finally {
    //         setLoading(false); // Set loading to false after data has been fetched (both on success and error)
    //     }
    // };

    //for error handling
    const [iserror, setIserror] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    const [open, setOpen] = React.useState(false);
    const handleClickOpen = () => {
        setOpen(true);
    };

    // * Add Filters to State Start
    const [selectedRows, setSelectedRows] = useState([]);

    // const [components] = useState({
    // 	Toolbar = props => <MTableToolbar {...props} />,
    // })

    const [columns, setColumns] = useState([]);

    useEffect(() => {
        setColumns([
            {
                title: t('reports.discrepanciesReport.employeeID'),
                field: 'user_id',
                editable: 'never',
            },
            {
                title: t('reports.discrepanciesReport.employeeName'),
                field: 'user_name',
                editable: 'never',
                defaultSort: 'ASC',
            },
            {
                title: t('reports.discrepanciesReport.deviceLocation'),
                field: 'location_name',
                editable: 'never',
            },
            {
                title: t('reports.discrepanciesReport.department'),
                field: 'sub_loc_name',
                lookup: departmentsOptions,
                editable: 'never',
            },
            {
                title: t('reports.discrepanciesReport.problem'),
                field: 'problem',
                editable: 'never',
                render: renderProblemCell,
            },
            {
                title: t('reports.discrepanciesReport.firstEntryTime'),
                field: 'first_entry_time',
                filtering: false,
                editable: 'never',
                type: 'datetime',
                dateSetting: {
                    locale: dateConfig.locale,
                    format: dateConfig.format,
                    hour12: false,
                },
                render: renderFirstEntryTimeCell,
            },
            {
                title: t('reports.discrepanciesReport.enterDevice'),
                field: 'enter_device_name',
                editable: 'never',
            },
            {
                title: t('reports.discrepanciesReport.lastExitTime'),
                field: 'last_exit_time',
                filtering: false,
                editable: 'never',
                type: 'datetime',
                dateSetting: {
                    locale: dateConfig.locale,
                    format: dateConfig.format,
                    hour12: false,
                },
                render: renderLastExitTimeCell,
            },
            {
                title: t('reports.discrepanciesReport.exitDevice'),
                field: 'exit_device_name',
                editable: 'never',
            },
        ]);
    }, [t, departmentsOptions, dateConfig]);
    // * Add Filters to State End

    const [components] = useState({
        Toolbar: props => (
            <div>
                <MTableToolbar {...props} />
            </div>
        ),
        FilterRow: props => <MTableFilterRow {...props} />,
    });

    // ! Format the Date and time columns for export
    const formatDate = inputTime => {
        if (inputTime && inputTime !== 'null' && inputTime !== 'empty') {
            const date = toTzDate(inputTime);
            return dateConfig.dateFormatExport(date);
        }
        return 'empty';
    };

    // ! Format only date part (without time) for exports
    const formatDateOnly = inputDate => {
        if (inputDate && inputDate !== 'null' && inputDate !== 'empty') {
            const date = toTzDate(inputDate);
            if (i18n.language === 'bg') {
                return `${date
                    .getDate()
                    .toString()
                    .padStart(2, '0')}.${(date.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}.${date.getFullYear()} г.`;
            } else if (i18n.language === 'en') {
                return `${(date.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}/${date
                    .getDate()
                    .toString()
                    .padStart(2, '0')}/${date.getFullYear()}`;
            } else if (i18n.language === 'es') {
                return `${date
                    .getDate()
                    .toString()
                    .padStart(2, '0')}/${(date.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}/${date.getFullYear()}`;
            }
            // Default format
            return `${date
                .getDate()
                .toString()
                .padStart(2, '0')}.${(date.getMonth() + 1)
                .toString()
                .padStart(2, '0')}.${date.getFullYear()}`;
        }
        return 'empty';
    };

    // ! Header mapping for exported column titles
    const headerMapping = {};
    columns.forEach(col => {
        headerMapping[col.field] = col.title;
    });

    const mapHeaders = row => {
        let newRow = {};
        for (let key in row) {
            if (headerMapping[key]) {
                newRow[headerMapping[key]] = row[key];
            } else {
                newRow[key] = row[key];
            }
        }
        return newRow;
    };

    // ! Format the name for the file
    const formatDateForFilename = inputDate => {
        const date = new Date(inputDate);
        return `${date.getFullYear()}-${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${date
            .getDate()
            .toString()
            .padStart(2, '0')}`;
    };

    const formattedStartDate = formatDateForFilename(selectedStartDate);
    const formattedEndDate = formatDateForFilename(selectedEndDate);

    // ! Export to XLS Function
    const tableRef = React.createRef();
    const exportTable = () => {
        const currentTableData = getMaterialTableExportData(tableRef);

        // Transform the data
        const transformedData = currentTableData.map(row => {
            const hours = row.worked_hours || 0; // Use 0 if worked_hours is null or undefined
            const mins = row.mins || 0; // Use 0 if mins is null or undefined

            // Check for << and >> conditions for first_entry_time and last_exit_time
            const firstTimeValue =
                row.first_entry_time === '<<'
                    ? t('reports.discrepanciesReport.missingEntry')
                    : formatDate(row.first_entry_time);
            const lastTimeValue =
                row.last_exit_time === '>>'
                    ? t('reports.discrepanciesReport.missingExit')
                    : formatDate(row.last_exit_time);

            // Translate the problem field
            let problemValue = row.problem;
            if (row.problem === 'Липсва вход') {
                problemValue = t('reports.discrepanciesReport.missingEntry');
            } else if (row.problem === 'Липсва изход') {
                problemValue = t('reports.discrepanciesReport.missingExit');
            }

            return {
                ...row,
                time: formatDate(row.time),
                first_entry_time: firstTimeValue,
                last_exit_time: lastTimeValue,
                worked_hours: `${hours} часа и ${mins} минути`,
                problem: problemValue,
            };
        });

        // Only include displayed columns in the export
        const exportedData = transformedData.map(row => {
            let newRow = {};
            columns.forEach(col => {
                newRow[col.field] = row[col.field];
            });
            return mapHeaders(newRow);
        });

        // Export the data
        const ws = XLSX.utils.json_to_sheet(exportedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        const buf = XLSX.write(wb, { bookType: 'xls', type: 'array' });
        saveAs(
            new Blob([buf], { type: 'application/vnd.ms-excel' }),
            `Discrepancies_${formattedStartDate} - ${formattedEndDate}.xls`
        );
    };

    const exportTableCsv = () => {
        const currentTableData = getMaterialTableExportData(tableRef);

        // Transform the data
        const transformedData = currentTableData.map(row => {
            const hours = row.worked_hours || 0; // Use 0 if worked_hours is null or undefined
            const mins = row.mins || 0; // Use 0 if mins is null or undefined

            // Check for << and >> conditions for first_entry_time and last_exit_time
            const firstTimeValue =
                row.first_entry_time === '<<'
                    ? t('reports.discrepanciesReport.missingEntry')
                    : formatDate(row.first_entry_time);
            const lastTimeValue =
                row.last_exit_time === '>>'
                    ? t('reports.discrepanciesReport.missingExit')
                    : formatDate(row.last_exit_time);

            // Translate the problem field
            let problemValue = row.problem;
            if (row.problem === 'Липсва вход') {
                problemValue = t('reports.discrepanciesReport.missingEntry');
            } else if (row.problem === 'Липсва изход') {
                problemValue = t('reports.discrepanciesReport.missingExit');
            }

            return {
                ...row,
                time: formatDate(row.time),
                first_entry_time: firstTimeValue,
                last_exit_time: lastTimeValue,
                worked_hours: `${hours} часа и ${mins} минути`,
                problem: problemValue,
            };
        });

        // Only include displayed columns in the export
        const exportedData = transformedData.map(row => {
            let newRow = {};
            columns.forEach(col => {
                newRow[col.field] = row[col.field];
            });
            return mapHeaders(newRow);
        });

        // Export the data
        const ws = XLSX.utils.json_to_sheet(exportedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        const buf = XLSX.write(wb, { bookType: 'csv', type: 'array' });
        saveAs(
            new Blob([buf], { type: 'application/vnd.ms-excel' }),
            `Discrepancies_${formattedStartDate} - ${formattedEndDate}.csv`
        );
    };

    return (
        <div>
            <Paper>
                <Grid
                    container
                    spacing={1}
                    className={classes.absoluteContainer}
                    style={{ paddingLeft: '16px' }}
                >
                    <Grid item xs={4} lg={3} style={{ paddingTop: '20px' }}>
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale={dateConfig.locale}
                        >
                            <MobileDatePicker
                                closeOnSelect={true}
                                id="selectedStartDate"
                                label={t('reports.common.startDate')}
                                ampm={false}
                                value={selectedStartDate}
                                onChange={date =>
                                    handleStartDateChange(
                                        normalizeToolbarStartDate(date)
                                    )
                                }
                                slotProps={reportDatePickerSlotProps}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4} lg={3} style={{ paddingTop: '20px' }}>
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale={dateConfig.locale}
                        >
                            <MobileDatePicker
                                closeOnSelect={true}
                                id="selectedEndDate"
                                label={t('reports.common.endDate')}
                                value={selectedEndDate}
                                onChange={date =>
                                    handleEndDateChange(
                                        normalizeToolbarEndDate(date)
                                    )
                                }
                                slotProps={reportDatePickerSlotProps}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4} lg={6}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className={classes.button}
                            onClick={retrieveFilteredDay}
                            disabled={isLoading} // Disable button when loading
                        >
                            {isLoading
                                ? t('reports.common.loading')
                                : t('reports.common.filter')}
                        </Button>
                    </Grid>
                </Grid>
                <MaterialTable
                    title=""
                    columns={[
                        {
                            title: t('reports.discrepanciesReport.employeeID'),
                            field: 'user_id',
                            editable: 'never',
                        },
                        {
                            title: t(
                                'reports.discrepanciesReport.employeeName'
                            ),
                            field: 'user_name',
                            editable: 'never',
                        },
                        {
                            title: t(
                                'reports.discrepanciesReport.deviceLocation'
                            ),
                            field: 'location_name',
                            editable: 'never',
                        },
                        {
                            title: t('reports.discrepanciesReport.department'),
                            field: 'sub_loc_name',
                            lookup: departmentsOptions,
                            editable: 'never',
                        },

                        {
                            title: t('reports.discrepanciesReport.problem'),
                            field: 'problem',
                            editable: 'never',
                            render: renderProblemCell,
                        },
                        {
                            title: t(
                                'reports.discrepanciesReport.firstEntryTime'
                            ),
                            field: 'first_entry_time',
                            filtering: false,
                            editable: 'never',
                            type: 'datetime',
                            dateSetting: {
                                locale: dateConfig.locale,
                                format: dateConfig.format,
                                hour12: false,
                            },
                            render: renderFirstEntryTimeCell,
                        },
                        {
                            title: t('reports.discrepanciesReport.enterDevice'),
                            field: 'enter_device_name',
                            editable: 'never',
                        },
                        {
                            title: t(
                                'reports.discrepanciesReport.lastExitTime'
                            ),
                            field: 'last_exit_time',
                            filtering: false,
                            editable: 'never',
                            type: 'datetime',
                            dateSetting: {
                                locale: dateConfig.locale,
                                format: dateConfig.format,
                                hour12: false,
                            },
                            render: renderLastExitTimeCell,
                        },
                        {
                            title: t('reports.discrepanciesReport.exitDevice'),
                            field: 'exit_device_name',
                            editable: 'never',
                        },
                    ]}
                    data={filteredDay || iserror}
                    tableRef={tableRef}
                    style={{
                        borderRadius: '0',
                        boxShadow: 'none',
                    }}
                    // actions={[
                    // 	{
                    // 		icon: "refresh",
                    // 		tooltip: "Refresh",
                    // 		isFreeAction: true,
                    // 		onClick: retrieveEvents,
                    // 	},
                    // ]}
                    components={{
                        components,
                        Body: props =>
                            !dataFetched ? (
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        margin: '15% auto',
                                        position: 'absolute',
                                        textAlign: 'center',
                                    }}
                                >
                                    {t(
                                        'reports.discrepanciesReport.selectPeriod'
                                    )}
                                </div>
                            ) : (
                                <MTableBody {...props} />
                            ),
                    }}
                    actions={[
                        ...(!isAnalyzer
                            ? [
                                  {
                                      icon: () => <DownloadOutlinedIcon />,
                                      tooltip: 'Export to CSV',
                                      isFreeAction: true,
                                      onClick: exportTableCsv,
                                  },
                                  {
                                      icon: () => <DescriptionIcon />,
                                      tooltip: 'Export to Excel',
                                      isFreeAction: true,
                                      onClick: exportTable,
                                  },
                              ]
                            : []),
                    ]}
                    options={{
                        loadingType: 'linear',
                        pageSize: 100,
                        minBodyHeight: '67vh',
                        maxBodyHeight: '67vh',
                        showTitle: true,
                        sorting: true,
                        actionsColumnIndex: -1,
                        filtering: true,
                        pageSizeOptions: [20, 50, 100, 200, 300, 400, 500],
                        search: false,
                        toolbar: true,
                    }}
                    // editable={{
                    // 	onRowUpdate: oldEvent =>
                    // 		new Promise(resolve => {
                    // 			handleEventUpdate(oldEvent, resolve)
                    // 			retrieveEvents()
                    // 		}),
                    // 	onRowDelete: Event =>
                    // 		new Promise(resolve => {
                    // 			handleEventRemove(Event, resolve)
                    // 			retrieveEvents()
                    // 		}),
                    // }}
                    onSelectionChange={rows => {
                        setSelectedRows(rows);
                    }}
                    localization={{
                        pagination: {
                            labelDisplayedRows:
                                '{from}-{to} ' +
                                t('reports.common.of') +
                                ' {count}',
                            labelRowsPerPage: t('reports.common.rowsPerPage'),
                            labelRowsSelect: t('reports.common.rowsSelect'),
                        },
                        header: {
                            actions: t('reports.common.actions'),
                        },
                        body: {
                            emptyDataSourceMessage: t(
                                'reports.common.emptyDataSourceMessage'
                            ),
                            editTooltip: t('reports.common.edit'),
                            deleteTooltip: t('reports.common.delete'),
                            filterRow: {
                                filterTooltip: t('reports.common.filter'),
                            },
                            editRow: {
                                deleteText: t(
                                    'reports.common.deleteConfirmation'
                                ),
                                cancelTooltip: t('reports.common.cancel'),
                                saveTooltip: t('reports.common.save'),
                            },
                        },
                        toolbar: {
                            exportTitle: t('reports.common.export'),
                            exportName: t('reports.common.exportCSV'),
                            searchTooltip: t('reports.common.search'),
                            searchPlaceholder: t(
                                'reports.common.searchPlaceholder'
                            ),
                            nRowsSelected: t('reports.common.rowsSelected', {
                                count: selectedRows.length,
                            }),
                        },
                    }}
                />
            </Paper>
        </div>
    );
};

export default DiscrepanciesTable;
